import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/LoadingButton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * Props for the SyncConfirmationDialog component
 */
interface SyncConfirmationDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Handler for open state changes */
  onOpenChange: (open: boolean) => void;
  /** Handler called when sync is confirmed */
  onConfirm: () => void;
  /** Whether the sync operation is in progress */
  isSyncing?: boolean;
}

/**
 * Dialog for confirming Reset+Full sync operations
 */
export function SyncConfirmationDialog({
  open,
  onOpenChange,
  onConfirm,
  isSyncing = false,
}: SyncConfirmationDialogProps) {
  const { t } = useLanguage();
  const [acknowledged, setAcknowledged] = useState(false);

  // Reset acknowledged state when dialog opens/closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setAcknowledged(false);
    }
    onOpenChange(newOpen);
  };

  const handleConfirm = () => {
    onConfirm();
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <DialogTitle>{t.dialogs.syncConfirmation.titleReset}</DialogTitle>
          </div>
          <DialogDescription>
            {t.dialogs.syncConfirmation.descriptionReset}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="bg-destructive/10 border-destructive/20 border rounded-md p-4">
            <p className="text-sm text-destructive font-medium mb-2">
              {t.dialogs.syncConfirmation.warningReset}
            </p>
            <p className="text-sm text-muted-foreground mb-2">{t.dialogs.syncConfirmation.warningResetOperations}</p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>{t.dialogs.syncConfirmation.warningResetOperation1}</li>
              <li>{t.dialogs.syncConfirmation.warningResetOperation2}</li>
              <li>{t.dialogs.syncConfirmation.warningResetOperation3}</li>
            </ul>
          </div>
        </div>
        <div className="flex items-start space-x-2 py-2">
          <Checkbox
            id="acknowledge-reset"
            checked={acknowledged}
            onCheckedChange={(checked) => { setAcknowledged(checked === true); }}
            className="mt-1"
          />
          <label
            htmlFor="acknowledge-reset"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
          >
            {t.dialogs.syncConfirmation.acknowledgeReset}
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { handleOpenChange(false); }} disabled={isSyncing}>
            {t.dialogs.syncConfirmation.cancel}
          </Button>
          <LoadingButton
            variant="destructive"
            onClick={handleConfirm}
            disabled={!acknowledged}
            isLoading={isSyncing}
          >
            {t.dialogs.syncConfirmation.resetAndSync}
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
